import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2, Save } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  RadioGroup,
  RadioGroupItem,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  type LearningContentLanguage,
  type LearningDefaultsUpdate,
  useLearningDefaults,
  useUpdateLearningDefaults,
} from "@/hooks/useLearningDefaults";

const LANGUAGES: ReadonlyArray<{ value: LearningContentLanguage; label: string }> = [
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi (हिंदी)" },
];

function parseCount(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function LearningDefaultsSettingsPage() {
  const configQuery = useLearningDefaults();
  const saveMutation = useUpdateLearningDefaults();

  const [maxQuestions, setMaxQuestions] = React.useState("50");
  const [diagnosticCount, setDiagnosticCount] = React.useState("8");
  const [practiceCount, setPracticeCount] = React.useState("8");
  const [postReviewCount, setPostReviewCount] = React.useState("3");
  const [skillCount, setSkillCount] = React.useState("5");
  const [language, setLanguage] = React.useState<LearningContentLanguage>("en");
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (!configQuery.data) return;
    setMaxQuestions(String(configQuery.data.max_questions_per_quiz));
    setDiagnosticCount(String(configQuery.data.default_diagnostic_question_count));
    setPracticeCount(String(configQuery.data.default_practice_question_count));
    setPostReviewCount(String(configQuery.data.default_post_review_question_count));
    setSkillCount(String(configQuery.data.default_skill_assessment_question_count));
    setLanguage(configQuery.data.default_content_language);
    setDirty(false);
  }, [configQuery.data]);

  const body: LearningDefaultsUpdate = {
    max_questions_per_quiz: parseCount(maxQuestions, 50),
    default_diagnostic_question_count: parseCount(diagnosticCount, 8),
    default_practice_question_count: parseCount(practiceCount, 8),
    default_post_review_question_count: parseCount(postReviewCount, 3),
    default_skill_assessment_question_count: parseCount(skillCount, 5),
    default_content_language: language,
  };

  const canSave = dirty && !saveMutation.isPending && body.max_questions_per_quiz >= 1;

  async function handleSave() {
    try {
      await saveMutation.mutateAsync(body);
      toast.success("Learning defaults updated.");
      setDirty(false);
    } catch {
      toast.error("Couldn't save learning defaults. Try again in a moment.");
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-12 lg:p-8">
      <PageHeader>
        <PageHeaderTop>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="size-4" aria-hidden="true" />
              Settings
            </Link>
          </Button>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>Learning defaults</PageHeaderTitle>
          <PageHeaderDescription>
            Institution-wide caps for AI quizzes and the default content language learners see in
            the Learn tab.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      {configQuery.isLoading ? (
        <Card>
          <CardContent className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Loading…
          </CardContent>
        </Card>
      ) : configQuery.isError ? (
        <Card>
          <CardContent className="text-destructive py-8 text-sm">
            Learning defaults are only available for study centres.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <BookOpen className="text-muted-foreground size-5" aria-hidden="true" />
              <CardTitle>Quiz policy</CardTitle>
            </div>
            <CardDescription>
              Learners cannot exceed the max per quiz. Per-learner overrides can tighten further
              from the learner editor.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            <div className="grid max-w-xs gap-2">
              <Label htmlFor="ia-learning-max">Max questions per quiz</Label>
              <Input
                id="ia-learning-max"
                inputMode="numeric"
                value={maxQuestions}
                onChange={(e) => {
                  setMaxQuestions(e.target.value);
                  setDirty(true);
                }}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <CountField
                id="ia-learning-diagnostic"
                label="Default diagnostic count"
                value={diagnosticCount}
                onChange={(v) => {
                  setDiagnosticCount(v);
                  setDirty(true);
                }}
              />
              <CountField
                id="ia-learning-practice"
                label="Default practice count"
                value={practiceCount}
                onChange={(v) => {
                  setPracticeCount(v);
                  setDirty(true);
                }}
              />
              <CountField
                id="ia-learning-post-review"
                label="Default post-review count"
                value={postReviewCount}
                onChange={(v) => {
                  setPostReviewCount(v);
                  setDirty(true);
                }}
              />
              <CountField
                id="ia-learning-skill"
                label="Default skill assessment count"
                value={skillCount}
                onChange={(v) => {
                  setSkillCount(v);
                  setDirty(true);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Default content language</Label>
              <RadioGroup
                value={language}
                onValueChange={(next) => {
                  setLanguage(next as LearningContentLanguage);
                  setDirty(true);
                }}
                className="grid gap-2 sm:grid-cols-2"
              >
                {LANGUAGES.map((opt) => (
                  <label
                    key={opt.value}
                    className={cn(
                      "border-border/60 flex cursor-pointer items-center gap-3 rounded-lg border p-3",
                      language === opt.value && "border-primary/40 bg-muted/30",
                    )}
                  >
                    <RadioGroupItem value={opt.value} />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <Button type="button" disabled={!canSave} onClick={() => void handleSave()}>
              {saveMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-4" aria-hidden="true" />
              )}
              Save
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CountField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
